import type { Chess } from "@vendor/chess.js";
import type { BlackStructurePlan, BlackTheme } from "./black/types.ts";
import type { CourseTheme } from "./themeDetection.ts";
import type { BookCoachingNote, BoundaryGuide } from "../trainer/bookCoaching.ts";

/**
 * Coaching for a White course that ships with the course body instead of the
 * first download. Every White course registers one, so adding a course does not
 * grow what every visit downloads.
 * The course loader registers the pack when the body arrives
 * (see whiteCourses.ts). It lives with the course
 * loaders, not the trainer, so the course chunk never imports the trainer.
 */
export type WhiteCoachingPack = {
  /**
   * One note per White main-line move, 1 to 15, in order. From move 3 each
   * note carries its strategic depth; authored alternatives sit in options.
   */
  book: BookCoachingNote[];
  /** Advice after leaving the course: moves 1–4, 5–7 and 8 onward. */
  boundary: readonly [BoundaryGuide, BoundaryGuide, BoundaryGuide];
  /** Course ideas, detected from White's moves (same fields as a Black course's themes). */
  themes: BlackTheme[];
  /**
   * Plans for pawn structures, first match wins. Steps are done once any of
   * their White pieces stands on the board.
   */
  plans: BlackStructurePlan[];
  /**
   * Themes whose detection needs code (the original six courses). When present
   * they are used instead of `themes`.
   */
  codeThemes?: CourseTheme[];
  /** A structure plan written as code, tried after `plans`; null falls back to general advice. */
  planFor?: (chess: Chess) => BoundaryGuide | null;
};

const PACKS = new Map<string, WhiteCoachingPack>();
const listeners = new Set<() => void>();

export function registerWhiteCoaching(openingId: string, pack: WhiteCoachingPack): void {
  PACKS.set(openingId, pack);
  for (const listener of listeners) listener();
}

/** The pack for a loaded course, or undefined (not a pack course, or not loaded yet). */
export function whiteCoachingPack(
  openingId: string | null | undefined
): WhiteCoachingPack | undefined {
  return openingId ? PACKS.get(openingId) : undefined;
}

export function registeredWhiteCoaching(): [string, WhiteCoachingPack][] {
  return [...PACKS];
}

/** Called after any registration, so caches built from the packs can be dropped. */
export function onWhiteCoachingRegistered(listener: () => void): void {
  listeners.add(listener);
}
